import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useDebouncedValue } from '@/containers/DevTools/Feedback/useDebounceValue'
import {
  setSearch,
  setPage,
  toggleSort,
  setShowOnlyDeleted,
  setShowOnlyActive,
  setConfirmAction,
  setSelectedProductType,
  updateResourceOfProductType,
} from '../AdditionalSalesManagement/additionalProductsToolsSlice'
import { useSnackbar } from 'baseui/snackbar'
import { additionalProductsToolsActions } from '../AdditionalSalesManagement/additionalProductsToolsSagas'
import { selectAdditionalProductsToolsState } from '../AdditionalSalesManagement/additionalProductsToolsSelectors'
import useModal from '@/shared/hooks/useModal'
import { selectAuthData } from '../Login/AuthSelectors'

export default function useAdditionalProductsTools() {
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { isOpen, close, setIsOpen } = useModal()
  const { tinymceKey } = useSelector(selectAuthData)

  const state = useSelector(selectAdditionalProductsToolsState)
  const {
    items,
    search,
    page,
    pageSize,
    sortKey,
    sortDirection,
    showOnlyDeleted,
    showOnlyActive,
    confirmAction,
    isLoading,
    error,
    actionLoadingId,
    loaded,
    selectedProductType,
    resources,
    isSavingResources,
  } = state

  const debouncedSearch = useDebouncedValue(search, 500)

  useEffect(() => {
    if (!loaded) {
      dispatch({ type: additionalProductsToolsActions.FETCH_LIST, enqueue })
    }
  }, [loaded])

  const filteredItems = useMemo(() => {
    let filteredElements = items
    if (showOnlyDeleted && !showOnlyActive) filteredElements = filteredElements.filter(p => p.deleted_at)
    if (showOnlyActive && !showOnlyDeleted) filteredElements = filteredElements.filter(p => !p.deleted_at)
    if (selectedProductType) filteredElements = filteredElements.filter(product => product.type === selectedProductType[0]?.id)
    if (!debouncedSearch) return filteredElements
    const term = debouncedSearch.toLowerCase()
    return filteredElements.filter(product =>
      [product.name, product.type, product.country, product.business_type, product.zoho_id].some(value =>
        (value || '').toString().toLowerCase().includes(term),
      ),
    )
  }, [items, debouncedSearch, showOnlyDeleted, showOnlyActive, selectedProductType])

  const sorted = useMemo(() => {
    const list = [...filteredItems]
    list.sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (av == null && bv != null) return -1
      if (av != null && bv == null) return 1
      if (av == null && bv == null) return 0
      if (av < bv) return sortDirection === 'ASC' ? -1 : 1
      if (av > bv) return sortDirection === 'ASC' ? 1 : -1
      return 0
    })
    return list
  }, [filteredItems, sortKey, sortDirection])

  const numPages = Math.max(1, Math.ceil(sorted.length / pageSize))
  const pageSafe = Math.min(page, numPages)
  const paginated = sorted.slice((pageSafe - 1) * pageSize, pageSafe * pageSize)

  const handleDelete = product => {
    dispatch({ type: additionalProductsToolsActions.DELETE, id: product.id, name: product.name, enqueue })
  }

  const handleRestore = product => {
    dispatch({ type: additionalProductsToolsActions.RESTORE, id: product.id, name: product.name, enqueue })
  }

  const updateResources = (type, value) => {
    const productType = selectedProductType[0]?.value
    if (!productType) return
    const currentResources = resources[productType] || []
    const otherResources = currentResources.filter(resource => resource.resource_type !== type)
    const updatedResources = [...otherResources, { resource_type: type, resource_value: value }]
    dispatch(updateResourceOfProductType({ productType, resources: updatedResources }))
  }

  const distinctProductTypes = []
  const alreadyAddedTypes = new Set()
  items.forEach(product => {
    if (product.type && !alreadyAddedTypes.has(product.type)) {
      const option = { id: product.type, label: product.type, value: product.type }
      distinctProductTypes.push(option)
      alreadyAddedTypes.add(product.type)
    }
  })

  const saveResources = () => {
    const productType = selectedProductType[0]?.value
    if (!productType) return
    const resourcesToSave = resources[productType] || []
    dispatch({
      type: additionalProductsToolsActions.UPDATE_RESOURCES,
      productType,
      resources: resourcesToSave,
      enqueue,
      callback: () => setIsOpen(false),
    })
  }

  return {
    search,
    page: pageSafe,
    sortKey,
    sortDirection,
    showOnlyDeleted,
    showOnlyActive,
    confirmAction,
    isLoading,
    error,
    actionLoadingId,
    debouncedSearch,
    paginated,
    filteredTotal: filteredItems.length,
    numPages,
    distinctProductTypes,
    selectedProductType,
    resources,
    tinymceKey,
    updateResources,
    saveResources,
    isSavingResources,
    setProductType: value => dispatch(setSelectedProductType(value)),
    setSearch: value => dispatch(setSearch(value)),
    setPage: value => dispatch(setPage(value)),
    handleSort: key => dispatch(toggleSort(key)),
    toggleShowOnlyDeleted: () => dispatch(setShowOnlyDeleted()),
    toggleShowOnlyActive: () => dispatch(setShowOnlyActive()),
    openDeleteModal: product => dispatch(setConfirmAction({ type: 'delete', product })),
    openRestoreModal: product => dispatch(setConfirmAction({ type: 'restore', product })),
    closeModal: () => dispatch(setConfirmAction(null)),
    confirmActionAndExecute: () => {
      if (!confirmAction) return
      const { type, product } = confirmAction
      dispatch(setConfirmAction(null))
      if (type === 'delete') handleDelete(product)
      else handleRestore(product)
    },
    handleDelete,
    handleRestore,
    isOpen,
    close,
    setIsOpen,
  }
}
